import i18n from '@/i18n/config';
import { RiPlayLine, RiEdit2Line, RiStopLine, RiCloseCircleLine, RiShieldCrossLine } from '@remixicon/react';
import type { ReactNode } from 'react';
import React from 'react';

// Experiment Status Type - supports both new lifecycle (active, completed, blocked, cancelled) and legacy (draft, running, finished)
export type ExperimentStatus =
  | 'active'
  | 'completed'
  | 'blocked'
  | 'cancelled'
  | 'draft'
  | 'running'
  | 'finished';

// Experiment Status Configuration
export interface ExperimentStatusConfig {
  label: string;
  description: string;
  color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  iconKey: 'active' | 'draft' | 'paused' | 'completed' | 'blocked';
}

// Icon mapping - semantic icons for lifecycle states
const STATUS_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  active: RiPlayLine,
  running: RiPlayLine,
  draft: RiEdit2Line,
  completed: RiStopLine,
  finished: RiStopLine,
  cancelled: RiCloseCircleLine,
  blocked: RiShieldCrossLine
};

// Experiment Status Definitions - new lifecycle with semantic colors:
// Active=Green, Completed=Primary, Stopped/Blocked=Gray-Orange, Cancelled=Red
export const EXPERIMENT_STATUSES: Record<string, ExperimentStatusConfig> = {
  active: {
    label: i18n.t('experimentStatus.active.label'),
    description: i18n.t('experimentStatus.active.description'),
    color: 'success',
    iconKey: 'active'
  },
  running: {
    label: i18n.t('experimentStatus.active.label'),
    description: i18n.t('experimentStatus.running.description'),
    color: 'success',
    iconKey: 'active'
  },
  draft: {
    label: i18n.t('experimentStatus.draft.label'),
    description: i18n.t('experimentStatus.draft.description'),
    color: 'default',
    iconKey: 'draft'
  },
  completed: {
    label: i18n.t('experimentStatus.completed.label'),
    description: i18n.t('experimentStatus.completed.description'),
    color: 'primary',
    iconKey: 'completed'
  },
  finished: {
    label: i18n.t('experimentStatus.completed.label'),
    description: i18n.t('experimentStatus.finished.description'),
    color: 'primary',
    iconKey: 'completed'
  },
  blocked: {
    label: i18n.t('experimentStatus.blocked.label'),
    description: i18n.t('experimentStatus.blocked.description'),
    color: 'warning',
    iconKey: 'blocked'
  },
  cancelled: {
    label: i18n.t('experimentStatus.cancelled.label'),
    description: i18n.t('experimentStatus.cancelled.description'),
    color: 'danger',
    iconKey: 'paused'
  }
};

// Helper function to get status label
export const getStatusLabel = (status: string): string => {
  return EXPERIMENT_STATUSES[status]?.label || status;
};

// Helper function to get status description
export const getStatusDescription = (status: string): string => {
  return EXPERIMENT_STATUSES[status]?.description || '';
};

// Helper function to get status color for Chip component
export const getStatusColor = (
  status: string
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
  return EXPERIMENT_STATUSES[status]?.color || 'default';
};

// Helper function to get status icon
export const getStatusIcon = (status: string): ReactNode => {
  const IconComponent = STATUS_ICONS[status];
  if (!IconComponent) return null;
  return React.createElement(IconComponent, { className: 'h-3 w-3' });
};

/** Statuses that allow cancelling (active/running/draft) */
export const isCancellableStatus = (status: string): boolean =>
  ['active', 'running', 'draft'].includes(status);

/** Statuses that allow blocking (active/running/draft) */
export const isBlockableStatus = (status: string): boolean =>
  ['active', 'running', 'draft'].includes(status);

// Canonical status keys for filters/dropdowns (no duplicates from legacy aliases)
const CANONICAL_STATUS_KEYS = ['active', 'completed', 'blocked', 'cancelled'] as const;

// Get all canonical status keys
export const getAllExperimentStatuses = (): string[] => {
  return [...CANONICAL_STATUS_KEYS];
};

// Get status options for filters/dropdowns
export const getStatusOptions = () => {
  return CANONICAL_STATUS_KEYS.map((status) => ({
    key: status,
    label: getStatusLabel(status)
  }));
};

// Get status order for sorting (lower number = higher priority)
export const getStatusOrder = (status: string): number => {
  const order: Record<string, number> = {
    active: 0,
    running: 0,
    draft: 1,
    completed: 2,
    finished: 2,
    blocked: 3,
    cancelled: 4
  };
  return order[status] ?? 999;
};

// Experiment type constants (backend uses uppercase: RECORDING, HEATMAP)
export const EXPERIMENT_TYPE_LABELS: Record<string, string> = {
  RECORDING: 'Recording',
  HEATMAP: 'Heatmap',
  recording: 'Recording',
  heatmap: 'Heatmap'
};

export const getExperimentTypeLabel = (type: string): string =>
  EXPERIMENT_TYPE_LABELS[type] ?? type;

export const getExperimentTypeColor = (
  type: string
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
  const upper = type.toUpperCase();
  if (upper === 'RECORDING') return 'primary';
  if (upper === 'HEATMAP') return 'secondary';
  return 'default';
};

export const EXPERIMENT_TYPE_OPTIONS = [
  { key: 'RECORDING', label: i18n.t('enums.recording') },
  { key: 'HEATMAP', label: i18n.t('enums.heatmap') }
];

/** Resolve experiment types array from API (types or legacy type) */
export function resolveExperimentTypes(item: { types?: string[]; type?: string }): string[] {
  const types = item.types;
  if (Array.isArray(types) && types.length > 0) return types;
  const legacy = item.type;
  return legacy ? [legacy.toUpperCase()] : [];
}

